import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Card, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, Menu, MenuItem, Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import api from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentEyebrowSx,
  documentPageContainerSx,
  documentSolidButtonSx,
} from '../constants/documentTheme';
import { formatMemoryDateRange, mediaUrl, memoriesPath } from '../utils/memoriesHost';
import { memoriesAuthEntryUrls } from '../utils/memoriesAuthBridge';
import { memoriesWordmarkSx } from '../constants/memoriesBrand';

const coverBadgeSx = {
  fontWeight: 500,
  fontSize: 9.5,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  height: 22,
  borderRadius: 0,
  backgroundColor: '#C9A961',
  color: '#2A070C',
  border: 0,
  '& .MuiChip-label': { px: 1.1 },
};

const MemoriesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviteBusyCode, setInviteBusyCode] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuEvent, setMenuEvent] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);

  const loadEvents = async () => {
    const { data } = await api.get('/memories/events');
    const list = Array.isArray(data.events)
      ? data.events
      : [
        ...(data.events?.present || []),
        ...(data.events?.future || []),
        ...(data.events?.past || []),
      ];
    setEvents(list);
  };

  const loadInbox = async () => {
    const { data } = await api.get('/memories/invites/inbox');
    setInbox(Array.isArray(data.invites) ? data.invites : []);
  };

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setEvents([]);
      setInbox([]);
      setLoading(false);
      setError('');
      return undefined;
    }
    (async () => {
      try {
        await Promise.all([loadEvents(), loadInbox()]);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || t('memories.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t, user]);

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuEvent(null);
  };

  const openMenu = (event, memoryEvent) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuEvent(memoryEvent);
  };

  const handleEdit = () => {
    const slug = menuEvent?.slug;
    closeMenu();
    if (slug) navigate(memoriesPath(`e/${slug}/edit`));
  };

  const handleArchiveClick = () => {
    setArchiveTarget(menuEvent);
    closeMenu();
  };

  const confirmArchive = async () => {
    if (!archiveTarget?.slug) return;
    setArchiving(true);
    setError('');
    try {
      await api.patch(`/memories/events/${archiveTarget.slug}`, { status: 'archived' });
      setEvents((prev) => prev.filter((item) => item.id !== archiveTarget.id));
      setArchiveTarget(null);
    } catch (err) {
      setError(err.response?.data?.error || t('memories.archiveFailed'));
    } finally {
      setArchiving(false);
    }
  };

  const acceptInvite = async (invite) => {
    if (!invite?.code) return;
    setInviteBusyCode(invite.code);
    setError('');
    try {
      const { data } = await api.post(`/memories/invites/${invite.code}/accept`);
      setInbox((prev) => prev.filter((item) => item.code !== invite.code));
      await loadEvents();
      if (data.slug) {
        navigate(memoriesPath(`e/${data.slug}`));
      }
    } catch (err) {
      setError(err.response?.data?.error || t('memories.acceptInviteFailed'));
    } finally {
      setInviteBusyCode('');
    }
  };

  const declineInvite = async (invite) => {
    if (!invite?.code) return;
    setInviteBusyCode(invite.code);
    setError('');
    try {
      await api.post(`/memories/invites/${invite.code}/decline`);
      setInbox((prev) => prev.filter((item) => item.code !== invite.code));
    } catch (err) {
      setError(err.response?.data?.error || t('memories.declineInviteFailed'));
    } finally {
      setInviteBusyCode('');
    }
  };

  return (
    <Box sx={{ backgroundColor: DOCUMENT.canvas, minHeight: '100vh', pb: 8 }}>
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, pt: 6 }}>
        <Box sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'flex-end' },
          gap: 2.5,
          flexWrap: 'wrap',
          pb: 1.9,
          mb: 2,
          borderBottom: `1px solid ${DOCUMENT.ink}`,
        }}
        >
          <Box>
            <Typography sx={{ letterSpacing: '0.16em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.accent, mb: 1 }}>
              {t('memories.brand')}
            </Typography>
            <Typography
              sx={{
                ...memoriesWordmarkSx,
                fontSize: { xs: 40, md: 56 },
                letterSpacing: '0.18em',
                color: DOCUMENT.ink,
              }}
            >
              {t('memories.title')}
            </Typography>
          </Box>
          {user ? (
            <Button
              component={RouterLink}
              to={memoriesPath('new')}
              sx={{ ...documentSolidButtonSx, ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}
            >
              {t('memories.createSpace')}
            </Button>
          ) : null}
        </Box>

        {!user ? (
          <Box
            sx={{
              border: `1px solid ${DOCUMENT.rule}`,
              backgroundColor: DOCUMENT.surface,
              p: 3,
              maxWidth: 480,
            }}
            data-testid="memories-list-sign-in"
          >
            <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14, mb: 2 }}>
              {t('memories.listSignInBody')}
            </Typography>
            <Button
              component="a"
              href={memoriesAuthEntryUrls(
                typeof window !== 'undefined' ? window.location.href : memoriesPath()
              ).login}
              sx={{ ...documentSolidButtonSx, width: { xs: '100%', sm: 'auto' } }}
            >
              {t('nav.signIn')}
            </Button>
          </Box>
        ) : (
          <>
        {loading && <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />}
        {error && <Typography sx={{ color: '#9C3B2A', mb: 2 }}>{error}</Typography>}

        {!loading && inbox.length > 0 && (
          <Box sx={{ mb: 4 }} data-testid="memories-invite-inbox">
            <Typography sx={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3, mb: 1.5 }}>
              {t('memories.inboxInvites')}
              {' · '}
              {inbox.length}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {inbox.map((invite) => {
                const dates = formatMemoryDateRange(invite.starts_on, invite.ends_on);
                const busy = inviteBusyCode === invite.code;
                return (
                  <Box
                    key={invite.id}
                    sx={{
                      border: `1px solid ${DOCUMENT.ruleHard}`,
                      backgroundColor: DOCUMENT.surface,
                      px: 2.25,
                      py: 2,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { sm: 'center' },
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={documentEyebrowSx}>
                        {t('memories.inboxInvitedBy', { host: invite.host || t('memories.hostFallback') })}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: DOCUMENT.display,
                          fontWeight: 400,
                          fontSize: 22,
                          lineHeight: 1.2,
                          mt: 0.5,
                          color: DOCUMENT.ink,
                        }}
                      >
                        {invite.name}
                      </Typography>
                      {(invite.place_name || dates) && (
                        <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.5 }}>
                          {[invite.place_name, dates].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
                      <Button
                        disabled={busy}
                        onClick={() => acceptInvite(invite)}
                        sx={{
                          ...documentSolidButtonSx,
                          flex: { xs: 1, sm: 'none' },
                          px: 2.5,
                          py: 1,
                          fontSize: 11,
                        }}
                      >
                        {busy ? t('common.loading') : t('memories.acceptInvite')}
                      </Button>
                      <Button
                        disabled={busy}
                        onClick={() => declineInvite(invite)}
                        sx={{
                          flex: { xs: 1, sm: 'none' },
                          color: DOCUMENT.ink3,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          fontSize: 11,
                          borderRadius: 0,
                          border: `1px solid ${DOCUMENT.rule}`,
                          px: 2.5,
                          py: 1,
                        }}
                      >
                        {t('memories.declineInvite')}
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {!loading && !error && events.length === 0 && inbox.length === 0 && (
          <Typography sx={{ color: DOCUMENT.ink3 }}>{t('memories.emptyList')}</Typography>
        )}

        {!loading && events.length > 0 && (
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mt: inbox.length > 0 ? 0 : 1 }}>
            {events.map((event) => {
              const dates = formatMemoryDateRange(event.starts_on, event.ends_on);
              const coverSrc = mediaUrl(event.cover_url || event.cover_image_url);
              const isOwner = Boolean(event.is_owner)
                || (user?.id != null && String(event.owner_user_id) === String(user.id));
              return (
                <Grid item xs={12} sm={6} md={4} key={event.id}>
                  <Card
                    onClick={() => navigate(memoriesPath(`e/${event.slug}`))}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      bgcolor: DOCUMENT.surface,
                      border: `1px solid ${DOCUMENT.ruleHard}`,
                      borderRadius: 0,
                      boxShadow: 'none',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover': { borderColor: DOCUMENT.accent },
                    }}
                  >
                    {isOwner && (
                      <IconButton
                        aria-label={t('memories.spaceActions')}
                        size="small"
                        onClick={(clickEvent) => openMenu(clickEvent, event)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 4,
                          bgcolor: 'rgba(255,255,255,0.92)',
                          borderRadius: '2px',
                          border: `1px solid ${DOCUMENT.rule}`,
                          '&:hover': { bgcolor: '#fff' },
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 18, color: DOCUMENT.ink }} />
                      </IconButton>
                    )}
                    <Box sx={{ position: 'relative', aspectRatio: '16 / 9', bgcolor: DOCUMENT.oxblood, overflow: 'hidden' }}>
                      {coverSrc ? (
                        <img
                          src={coverSrc}
                          alt=""
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <Box sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: DOCUMENT.display,
                          fontSize: 42,
                          color: DOCUMENT.ivory,
                        }}
                        >
                          {(event.name || 'M').slice(0, 1)}
                        </Box>
                      )}
                      <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(to top, rgba(18,5,8,0.55) 0%, rgba(18,5,8,0.12) 46%, transparent 74%)',
                        pointerEvents: 'none',
                      }}
                      />
                      {event.trip_id && (
                        <Chip
                          label={t('memories.tripBadge')}
                          size="small"
                          sx={{ ...coverBadgeSx, position: 'absolute', top: 13, left: 15, zIndex: 3 }}
                        />
                      )}
                    </Box>
                    <Box sx={{ px: 2.25, pt: 1.5, pb: 0, flex: 1 }}>
                      <Typography sx={documentEyebrowSx}>
                        {event.trip_id ? t('memories.tripBadge') : t('memories.standalone')}
                      </Typography>
                      <Typography
                        component="h3"
                        sx={{
                          fontFamily: DOCUMENT.display,
                          fontWeight: 400,
                          fontSize: 23,
                          lineHeight: 1.14,
                          mt: 0.75,
                          mb: 0.5,
                          color: DOCUMENT.ink,
                        }}
                      >
                        {event.name}
                      </Typography>
                      {event.place_name && (
                        <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>
                          {event.place_name}
                        </Typography>
                      )}
                      <Box component="dl" sx={{ mt: 1.75, mb: 0, borderTop: `1px solid ${DOCUMENT.rule}` }}>
                        {[
                          dates ? { label: t('trip.ledger.dates'), value: dates } : null,
                          { label: t('memories.frames'), value: event.frame_count || 0 },
                          { label: t('memories.contributors'), value: event.member_count || 0 },
                          event.owner_display_name
                            ? { label: t('memories.createdBy'), value: event.owner_display_name }
                            : null,
                        ].filter(Boolean).map((row) => (
                          <Box
                            key={row.label}
                            component="div"
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1.75,
                              py: 1.1,
                              borderBottom: `1px solid ${DOCUMENT.rule}`,
                            }}
                          >
                            <Typography component="dt" sx={{ fontSize: 12.5, color: DOCUMENT.ink3, m: 0 }}>
                              {row.label}
                            </Typography>
                            <Typography component="dd" sx={{ fontSize: 13.5, fontVariantNumeric: 'tabular-nums', textAlign: 'right', m: 0, color: DOCUMENT.ink }}>
                              {row.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
          </>
        )}
      </Container>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEdit}>{t('common.edit')}</MenuItem>
        <MenuItem onClick={handleArchiveClick} sx={{ color: '#9C3B2A' }}>
          {t('memories.archiveSpace')}
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(archiveTarget)} onClose={() => !archiving && setArchiveTarget(null)}>
        <DialogTitle>{t('memories.archiveSpaceTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('memories.archiveSpaceBody', { name: archiveTarget?.name || '' })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveTarget(null)} disabled={archiving} sx={{ color: DOCUMENT.ink2 }}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={confirmArchive}
            disabled={archiving}
            sx={{ color: '#9C3B2A', letterSpacing: '0.12em', textTransform: 'uppercase' }}
          >
            {archiving ? t('common.loading') : t('memories.archiveSpaceConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemoriesList;
